import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { DEFAULT_LIVE_SESSION_PAGE_SIZE } from '@/routes/_layout/sessions';
import { FrequentTimeZones } from '@airreading/common';
import { classSessionsDrawerColumns } from './columns';

interface ClassSessionsDrawerContentProps {
  classId: string;
}

export function ClassSessionsDrawerContent({ classId }: ClassSessionsDrawerContentProps) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_LIVE_SESSION_PAGE_SIZE);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        studentGroupId: classId,
      },
    }),
  );

  const sessions = sessionsResp?.adminSessions || EmptyArray;
  const rowCount = sessionsResp?.total || 0;

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const columns = classSessionsDrawerColumns(FrequentTimeZones.UsCentral);

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({ pageIndex, pageSize });
        setPageIndex(newPagination.pageIndex);
        setPageSize(newPagination.pageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setPageSize(updater.pageSize);
      }
    },
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState="No sessions found for this class"
      errorState={<TableErrorState refetchAction={refetch} />}
      hasError={!!error}
      isLoading={isPending}
      showPagination={true}
      emptyStateClassName="translate-y-0"
      autoManagedColumnIds={['editSession']}
    />
  );
}
